import { useState, useEffect } from 'react';
import type { CmsSectionData } from './types';
import { getDefaultCmsSections } from './defaults';
import { api } from '../lib/api';

const CMS_CACHE_KEY = 'fyt_cms_sections';
const CMS_CACHE_TS_KEY = 'fyt_cms_sections_ts';
const SPINNER_DELAY_MS = 120; // only show spinner if fetch takes longer than this
const CACHE_TTL_MS = 5 * 60 * 1000; // refresh cache after 5 minutes

interface CachedCmsData {
  sections: CmsSectionData[];
  ts: number;
}

function readCache(): CachedCmsData | null {
  try {
    const raw = localStorage.getItem(CMS_CACHE_KEY);
    const ts = Number(localStorage.getItem(CMS_CACHE_TS_KEY));
    if (!raw || !ts) return null;
    const sections = JSON.parse(raw) as CmsSectionData[];
    if (!Array.isArray(sections) || sections.length === 0) return null;
    return { sections, ts };
  } catch {
    return null;
  }
}

function writeCache(sections: CmsSectionData[]) {
  try {
    localStorage.setItem(CMS_CACHE_KEY, JSON.stringify(sections));
    localStorage.setItem(CMS_CACHE_TS_KEY, String(Date.now()));
  } catch {
    // ignore storage errors
  }
}

const initialCache = typeof window !== 'undefined' ? readCache() : null;

export function useCmsSections(): {
  sections: CmsSectionData[];
  loading: boolean;
  error: Error | null;
} {
  // Initialise from localStorage so repeat visitors never see the loading spinner.
  const [sections, setSections] = useState<CmsSectionData[]>(initialCache?.sections || []);
  const [loading, setLoading] = useState(!initialCache);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    let spinnerTimer: ReturnType<typeof setTimeout> | null = null;
    let showedSpinner = false;

    // Try to render from cache immediately to avoid loading flash.
    const cached = readCache();
    const cacheExpired = cached ? Date.now() - cached.ts > CACHE_TTL_MS : true;
    if (cached && !cacheExpired) {
      setSections(cached.sections);
      setLoading(false);
      return;
    }
    if (cached) {
      // Cache exists but expired: keep showing it while fetching in background.
      setSections(cached.sections);
      setLoading(false);
    } else {
      // Delay showing the spinner for very fast connections/CDN hits.
      spinnerTimer = setTimeout(() => {
        if (!cancelled) {
          setLoading(true);
          showedSpinner = true;
        }
      }, SPINNER_DELAY_MS);
    }

    async function load() {
      try {
        const res = await api.get<CmsSectionData[]>(`/api/public/cms/sections?_t=${Date.now()}`);
        if (cancelled) return;
        if (res.success && res.data && res.data.length > 0) {
          setSections(res.data);
          setError(null);
          writeCache(res.data);
        } else {
          setSections(getDefaultCmsSections());
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('CMS fetch failed, falling back to defaults:', err);
        // Only fall back to hard-coded defaults if we have nothing to show.
        if (!cached) {
          setSections(getDefaultCmsSections());
        }
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) {
          if (spinnerTimer) clearTimeout(spinnerTimer);
          // Only clear loading if we had shown it (cached path already set it false).
          if (showedSpinner || !cached) {
            setLoading(false);
          }
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (spinnerTimer) clearTimeout(spinnerTimer);
    };
  }, []);

  return { sections, loading, error };
}
